import type { DownloadOperatingSystem, ReleaseManifest } from "@studycoach/release-manifest";
import { stableReleaseManifest } from "@studycoach/release-manifest/stable";

export const releaseManifest: ReleaseManifest = stableReleaseManifest;

export const stableReleaseSummary: Readonly<{
  version: string;
  label: string;
  summary: string;
}> = {
  version: "1.13.0",
  label: "去掉冗余，让学习主线更清楚",
  summary: "Today、课程路线、AI 助手与复习入口已集中收敛：继续学习只有一个主动作，路线与助手各用一个统一入口，Provider 复习由学习者明确启动；网站低频页面改为按需加载，并由启动体积门禁约束后续膨胀。macOS 与 Windows 工件仍为 beta。"
};

if (stableReleaseSummary.version !== releaseManifest.version) {
  throw new Error(`stable release summary ${stableReleaseSummary.version} does not match manifest ${releaseManifest.version}`);
}

export function detectedOperatingSystem(userAgent: string, platform = ""): DownloadOperatingSystem | null {
  const source = `${userAgent} ${platform}`.toLocaleLowerCase();
  if (/windows|win32|win64/u.test(source)) return "windows";
  if (/macintosh|mac os|macintel/u.test(source)) return "macos";
  return null;
}

export function releaseDate(iso: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Asia/Shanghai"
  }).format(new Date(iso));
}

export function readableBytes(bytes?: number): string | null {
  if (!bytes) return null;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

export type ReleasePublicationState = "draft" | "current" | "previous";

function releaseVersionParts(version: string): readonly [number, number, number] {
  const match = /^(\d+)\.(\d+)\.(\d+)$/u.exec(version);
  if (!match) throw new Error(`invalid release version: ${version}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

export function releasePublicationState(
  version: string,
  stableVersion = releaseManifest.version
): ReleasePublicationState {
  const [candidateMajor, candidateMinor, candidatePatch] = releaseVersionParts(version);
  const [stableMajor, stableMinor, stablePatch] = releaseVersionParts(stableVersion);
  for (const difference of [
    candidateMajor - stableMajor,
    candidateMinor - stableMinor,
    candidatePatch - stablePatch
  ]) {
    if (difference > 0) return "draft";
    if (difference < 0) return "previous";
  }
  return "current";
}

export function releaseNotesAnchor(version = releaseManifest.version): string {
  return `/releases#v-${version.replaceAll(".", "-")}`;
}
